/**
 * Client Portal — GET solicitud + GET …/sessions/{quoteId}/offer (OUW-64) + requisitos (OUW-63).
 *
 * Este módulo es el cliente del portal (whitelist). `intakeApplicationService.ts`
 * es el GET del form.
 *
 * Writes: `postOfferOutcome` (03D), `postConfirm` (03E MX / OUW-70) y los
 * POST/DELETE de requisitos (`uploadRequirementFile`, `deleteRequirementFile`,
 * `deliverRequirement`). Pay (OUW-67): el CTA live hace handoff a `payment_url`;
 * 03G lee `issuance_stage === first_payment_confirmed`.
 */
import { REACT_APP_BACKEND_API } from '../../config';
import { handleServiceError } from '../../api/errorHandler';
import { getHeadersApplication } from '../../api/middleware';

const BASE_URL = REACT_APP_BACKEND_API;

export const PORTAL_ANALYST_FIELDS = [
  'assigned_to',
  'available_actions',
  'automatic_evaluation_audit',
  'round',
  'auto_approved',
  'offer',
  'requirements_summary',
] as const;

export type PortalMoney = {
  amount: number;
  currency: string;
};

export type PortalPremium = PortalMoney & {
  frequency?: string;
};

/** OUW-88: seller contact from GET …/applications/:quoteId. */
export type PortalAgent = {
  name: string;
  phone: string;
  email: string;
};

export type PortalStatus = {
  quoteId: string;
  status: string;
  displayStatus: string;
  category: string;
  hasPendingRequirements: boolean;
  expiresAt: string | null;
  createdAt: string;
  /** Cuándo entró al estado actual. En 03H es la fecha del cierre. */
  stateEnteredAt: string | null;
  /**
   * El motivo del cierre, ya legible (`offer_not_accepted`). Único campo que
   * salió de PORTAL_ANALYST_FIELDS: en 03H el motivo es contenido del CLIENTE.
   * Se muestra SIEMPRE mapeado a copy — el código crudo nunca llega al DOM.
   */
  statusDetail: string;
  reference: string;
  applicantName: string;
  dateOfBirth: string;
  sumAssured: PortalMoney | null;
  premium: PortalPremium | null;
  termYears: number | null;
  /**
   * ETAPA-1 tarifador: las coberturas adicionales contratadas, como claves del
   * tarifador (`critical_illnesses`, `accidental_death`…). Los nombres salen
   * del catálogo `uwOfferCoverages`; una clave sin nombre se muestra cruda.
   */
  addOns: string[];
  agent: PortalAgent;
  /** Present after confirm + policy_approved (OUW-67). Handoff target, never rendered. */
  paymentUrl?: string;
  /**
   * Overlay after emission / first cobro. The machine stays `confirmed`;
   * `first_payment_confirmed` is what turns the board into 03G.
   */
  issuanceStage?: string;
  /**
   * UWT-79: el mercado firma (MX). false = INT, sin firma: el tablero no
   * muestra ningún texto de firma. Ausente (api-client viejo) = firma.
   */
  signatureRequired?: boolean;
};

type ApiSuccess<T> = { message?: string; data: T };

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : value == null ? '' : String(value);
}

function pickMoney(raw: unknown): PortalMoney | null {
  const row = asRecord(raw);
  const amount = Number(row.amount);
  if (!Number.isFinite(amount)) return null;
  return {
    amount,
    currency: asString(row.currency) || 'USD',
  };
}

/**
 * Whitelist: only the fields the Client Portal is allowed to keep.
 * Analyst / workbench fields are dropped even if the API sent them.
 */
export function pickPortalStatus(raw: unknown): PortalStatus {
  const row = asRecord(raw);
  const money = pickMoney(row.premium);
  const frequency = asString(asRecord(row.premium).frequency);
  const paymentUrl = asString(row.payment_url).trim();
  const issuanceStage = asString(row.issuance_stage).trim();
  const termYears = Number(row.term_years);
  // Solo strings no vacíos: una entrada rara del array se descarta, no se serializa cruda.
  const addOns = Array.isArray(row.add_ons)
    ? row.add_ons.filter((code): code is string => typeof code === 'string').map((code) => code.trim()).filter(Boolean)
    : [];
  const agentRow = asRecord(row.agent);
  const agentName = asString(agentRow.name) || asString(row.agent_name);

  return {
    quoteId: asString(row.quote_id),
    status: asString(row.status),
    displayStatus: asString(row.display_status),
    category: asString(row.category),
    hasPendingRequirements: Boolean(row.has_pending_requirements),
    expiresAt: asString(row.expires_at) || null,
    createdAt: asString(row.created_at),
    stateEnteredAt: asString(row.state_entered_at) || null,
    statusDetail: asString(row.status_detail),
    reference: asString(row.reference),
    applicantName: asString(row.applicant_name),
    dateOfBirth: asString(row.date_of_birth),
    sumAssured: pickMoney(row.sum_assured),
    premium: money
      ? { ...money, frequency: frequency || undefined }
      : null,
    termYears: Number.isFinite(termYears) && termYears > 0 ? termYears : null,
    addOns,
    agent: {
      name: agentName,
      phone: asString(agentRow.phone),
      email: asString(agentRow.email),
    },
    paymentUrl: paymentUrl || undefined,
    issuanceStage: issuanceStage || undefined,
    signatureRequired: row.signature_required !== false,
  };
}

function parseJsonSafe(response: Response): Promise<unknown> {
  return response.json().catch(() => ({}));
}

function messageFromBody(body: unknown, fallback: string): string {
  if (body && typeof body === 'object' && 'message' in body && (body as { message?: unknown }).message) {
    return String((body as { message: unknown }).message);
  }
  return fallback;
}

/** GET {REACT_APP_BACKEND_API}intake/applications/{quoteId}. 404 → null. */
export async function getPortalStatus(quoteId: string): Promise<PortalStatus | null> {
  const id = quoteId.trim();
  if (!id) return null;

  try {
    const response = await fetch(`${BASE_URL}intake/applications/${encodeURIComponent(id)}`, {
      method: 'GET',
      headers: getHeadersApplication(),
    });

    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      const body = await parseJsonSafe(response);
      throw new Error(messageFromBody(body, `Failed to load portal application: HTTP ${response.status}`));
    }

    const payload = (await response.json()) as ApiSuccess<unknown>;
    return pickPortalStatus(payload.data);
  } catch (error) {
    handleServiceError(error);
    throw error instanceof Error ? error : new Error('Failed to load portal application');
  }
}

/**
 * Respeta la moneda que vino de la API. Antes forzaba `USD` e ignoraba el campo
 * que `pickMoney` ya parsea: una solicitud en MXN habría mostrado la cobertura
 * en dólares mientras el panel de la oferta la mostraba en pesos, en la misma
 * pantalla y para el mismo campo.
 *
 * El locale se mantiene en `en-US` a propósito: es el que renderiza USD como
 * `$500,000`. En la página de estado se le agrega `USD` porque el portal de
 * pago cobra en dólares y un `$` solo no lo dice. Con MXN el mismo locale rinde
 * `MX$…`, que distingue sin ambigüedad. La red del `catch` es para un código de
 * moneda que `Intl` no conozca: se muestra el número con el código al lado en
 * vez de romper la pantalla.
 */
export function formatPortalMoney(amount: number, currency = 'USD'): string {
  const fractionDigits = Number.isInteger(amount) ? 0 : 2;
  const code = (currency.trim() || 'USD').toUpperCase();
  try {
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: 2,
    }).format(amount);
    if (code === 'USD' && !formatted.toUpperCase().includes('USD')) {
      return `${formatted} USD`;
    }
    return formatted;
  } catch {
    return `${amount} ${code}`;
  }
}

/**
 * El backend guarda y sirve los instantes en UTC; el cliente los lee en hora
 * del centro de México. Con UTC, confirmar a las 21:00 en CDMX mostraba el día
 * siguiente (y 03G tiene que coincidir con el inicio de vigencia de la póliza).
 */
const PORTAL_TIME_ZONE = 'America/Mexico_City';
/** `2026-09-02` sin hora ya es un día calendario: pasarlo a hora MX lo correría al día anterior. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * OUW-260: fecha Y hora, para la cita. `formatPortalDate` da solo el día, que
 * en una videollamada deja afuera el dato que importa.
 *
 * Siempre en hora de Ciudad de México: el documento guarda UTC y el cliente
 * tiene que leer la hora a la que se tiene que conectar, no la del servidor.
 */
export function formatPortalDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('es-MX', {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: PORTAL_TIME_ZONE,
  });
}

/**
 * El link de la videollamada que cargó el analista, solo si se puede abrir sin
 * riesgo. Mismo criterio que el link de pago: https, y http solo en local.
 * Un `javascript:` escrito a mano en el Workbench no se convierte en un botón.
 *
 * Tolera que el campo traiga MÁS que la URL: al agendar, lo natural es pegar
 * el bloque entero de Google Meet («Video call link: https://… Or dial: …»).
 * Con el texto completo `new URL()` falla, y el cliente se quedaba con la cita
 * confirmada y sin forma de entrar.
 */
export function meetingUrlOf(url: unknown): string | null {
  if (typeof url !== 'string') return null;
  const match = url.match(/https?:\/\/[^\s<>"']+/i);
  if (!match) return null;
  const candidate = match[0].replace(/[.,;:)\]}>]+$/, '');
  try {
    const parsed = new URL(candidate);
    if (parsed.protocol === 'https:') return candidate;
    return parsed.protocol === 'http:' && MEETING_LOCAL_HOSTS.has(parsed.hostname)
      ? candidate
      : null;
  } catch {
    return null;
  }
}

export function isSafeMeetingUrl(url: unknown): url is string {
  return meetingUrlOf(url) !== null;
}

const MEETING_LOCAL_HOSTS = new Set(['localhost', '127.0.0.1']);

export function formatPortalDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: DATE_ONLY.test(iso.trim()) ? 'UTC' : PORTAL_TIME_ZONE,
  });
}

type PremiumPeriod = 'M' | 'Q' | 'S' | 'A';

/**
 * El cotizador MX arma primas M, Q, S y A (`quotation_detail.frecuency` tal
 * cual). Sin frecuencia se asume mensual, como antes.
 */
function premiumPeriod(frequency?: string): PremiumPeriod {
  const value = (frequency || 'M').toUpperCase();
  if (value === 'Q' || value === 'QUARTERLY') return 'Q';
  if (value === 'S' || value === 'SEMIANNUAL' || value === 'SEMI_ANNUAL') return 'S';
  if (value === 'A' || value === 'Y' || value === 'ANNUAL' || value === 'YEARLY') return 'A';
  return 'M';
}

const PERIOD_SUFFIX: Record<PremiumPeriod, string> = {
  M: ' / mes',
  Q: ' / trimestre',
  S: ' / semestre',
  A: ' / año',
};

const PERIOD_ADJECTIVE: Record<PremiumPeriod, string> = {
  M: 'mensual',
  Q: 'trimestral',
  S: 'semestral',
  A: 'anual',
};

function frequencySuffix(frequency?: string): string {
  return PERIOD_SUFFIX[premiumPeriod(frequency)];
}

/** «mensual», «trimestral»…: para «Prima {period}» y «el débito {period}». */
export function frequencyAdjective(frequency?: string): string {
  return PERIOD_ADJECTIVE[premiumPeriod(frequency)];
}

export function formatPremiumLabel(premium: PortalPremium | null): string {
  if (!premium) return '';
  return formatPortalMoney(premium.amount, premium.currency);
}

export function formatPremiumRecurring(premium: PortalPremium | null): string {
  if (!premium) return '';
  return `${formatPortalMoney(premium.amount, premium.currency)}${frequencySuffix(premium.frequency)}`;
}

export function formatTermYears(years: number | null | undefined): string {
  if (!years) return '';
  return `${years} años`;
}

export function firstNameFrom(fullName: string): string {
  return fullName.trim().split(/\s+/)[0] || '';
}

export function coverageFrom(application: PortalStatus | null | undefined): string {
  if (!application?.sumAssured) return '';
  return formatPortalMoney(application.sumAssured.amount, application.sumAssured.currency);
}

export const PORTAL_OFFER_ANALYST_FIELDS = [
  'justification',
  'issued_by',
  'pricing',
  'decision_id',
  'resolution',
  'issued_at',
  'resolved_at',
] as const;

export type OfferOutcome = 'accepted' | 'rejected';

export type PortalOfferMoney = {
  amount: number;
  currency: string;
  frequency?: string;
};

export type PortalOfferAdjustment = {
  unit: string;
  direction: string;
  value: number;
  currency?: string;
};

export type PortalOfferItem = {
  code: string;
  text?: string;
  amount?: number;
};

export type PortalOfferChange = {
  field: string;
  current?: PortalOfferMoney | null;
  proposed?: PortalOfferMoney | null;
  adjustment?: PortalOfferAdjustment | null;
  currentItems?: PortalOfferItem[];
  proposedItems?: PortalOfferItem[];
  /** OUW-75: nota del evaluador para el cliente. Solo en prima. */
  clientNote?: string;
  /**
   * OUW-246: por qué cambió CADA cobertura, por código. Solo en `coverages`.
   * Es lo mismo que el cliente lee en la tarjeta de esa cobertura en el endoso:
   * sin esto, la pantalla explicaba el cambio de prima y no el de las
   * coberturas, justo mientras decide si acepta.
   */
  justifications?: Record<string, string>;
};

/**
 * OUW-246: la prima de UNA cobertura, antes y después. `code` es el de la
 * cobertura o `base` para la básica (Fallecimiento, con la extraprima adentro).
 * Cualquiera de los dos lados puede faltar: una cobertura que se agrega no tenía
 * precio antes, y una que se quita no lo tiene después.
 */
export type PortalOfferPremium = {
  code: string;
  before: number | null;
  after: number | null;
  /**
   * after − before. El lado que falta cuenta como cero: es la columna del
   * endoso. null cuando no se puede calcular.
   */
  difference: number | null;
};

export type PortalOffer = {
  /**
   * UWT-79: aceptar pasa por firmar el endoso (MX). false = INT: sin PDF ni
   * firma, el cliente acepta con el clic (`POST …/offer-outcome`).
   */
  signatureRequired?: boolean;
  id: string;
  round: number;
  status: string;
  expiresAt: string | null;
  changes: PortalOfferChange[];
  /**
   * OUW-246: cuánto cuesta cada cobertura. Es la misma tabla que el endoso le
   * imprime al cliente; sin esto la pantalla le mostraba el total y el motivo
   * del cambio, pero no el precio de lo que le sacan o le agregan.
   */
  premiums: PortalOfferPremium[];
  /**
   * OUW-246: la moneda de esos importes. Viaja aparte porque el desglose sellado
   * guarda números pelados. Vacía cuando la oferta no tiene pricing legible.
   */
  premiumsCurrency: string;
  /**
   * OUW-181: si el PDF de la propuesta ya está disponible. El documento se
   * genera de forma asíncrona al emitirse la oferta, así que hay una ventana
   * —corta, pero real— en la que la oferta existe y el documento todavía no.
   * Mientras sea false el panel espera: no se puede aceptar algo que el cliente
   * no pudo leer.
   */
  documentReady: boolean;
};

export class PortalHttpError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'PortalHttpError';
    this.status = status;
  }
}

export function isPortalHttpError(error: unknown): error is PortalHttpError {
  return error instanceof PortalHttpError;
}

function pickOfferMoney(raw: unknown): PortalOfferMoney | null {
  if (Array.isArray(raw)) return null;
  const money = pickMoney(raw);
  if (!money) return null;
  const frequency = asString(asRecord(raw).frequency);
  return frequency ? { ...money, frequency } : money;
}

function pickAdjustment(raw: unknown): PortalOfferAdjustment | null {
  const row = asRecord(raw);
  const value = Number(row.value);
  if (!Number.isFinite(value) || value <= 0) return null;
  const unit = asString(row.unit);
  const direction = asString(row.direction);
  if (!unit || !direction) return null;
  const currency = asString(row.currency);
  return {
    unit,
    direction,
    value,
    currency: currency || undefined,
  };
}

function pickOfferItems(raw: unknown): PortalOfferItem[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      const row = asRecord(entry);
      const code = asString(row.code);
      if (!code) return null;
      const text = asString(row.text).trim();
      const amount = Number(row.amount);
      const item: PortalOfferItem = { code };
      if (text) item.text = text;
      if (Number.isFinite(amount) && amount > 0) item.amount = amount;
      return item;
    })
    .filter((item): item is PortalOfferItem => Boolean(item));
}

function isListField(field: string): boolean {
  return field === 'coverages' || field === 'exclusions';
}

function pickClientNote(field: string, raw: Record<string, unknown>): string | undefined {
  if (field !== 'premium') return undefined;
  const note = asString(raw.client_note).trim();
  return note || undefined;
}

/** Los textos por cobertura. Se descarta lo que no sea texto con contenido. */
function pickJustifications(raw: unknown): Record<string, string> | undefined {
  const row = asRecord(raw);
  const out: Record<string, string> = {};
  for (const [code, value] of Object.entries(row)) {
    const text = asString(value).trim();
    if (code.trim() && text) out[code.trim()] = text;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export function pickPortalChange(raw: unknown): PortalOfferChange | null {
  const row = asRecord(raw);
  const field = asString(row.field).trim();
  if (!field) return null;
  const clientNote = pickClientNote(field, row);
  if (isListField(field)) {
    const justifications = pickJustifications(row.justifications);
    return {
      field,
      currentItems: pickOfferItems(row.current),
      proposedItems: pickOfferItems(row.proposed),
      ...(justifications ? { justifications } : {}),
    };
  }
  return {
    field,
    current: pickOfferMoney(row.current),
    proposed: pickOfferMoney(row.proposed),
    adjustment: pickAdjustment(row.adjustment),
    ...(clientNote ? { clientNote } : {}),
  };
}

function pickOfferPremium(raw: unknown): PortalOfferPremium | null {
  const row = asRecord(raw);
  const code = asString(row.code).trim();
  if (!code) return null;
  const money = (value: unknown): number | null => {
    // Number(null) y Number('') dan 0, no NaN. Sin descartarlos primero, un lado
    // vacío se volvería una prima de cero: el cliente leería que esa cobertura
    // ahora no le cuesta nada, cuando lo que pasa es que no tiene precio ahí.
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const before = money(row.before);
  const after = money(row.after);
  // Una fila sin ninguno de los dos lados no dice nada: no se dibuja.
  if (before === null && after === null) return null;
  const sent = money(row.difference);
  const difference =
    sent !== null
      ? sent
      : before !== null && after !== null
        ? Math.round((after - before) * 100) / 100
        : after !== null
          ? after
          : before !== null
            ? Math.round(-before * 100) / 100
            : null;
  return { code, before, after, difference };
}

export function pickPortalOffer(raw: unknown): PortalOffer {
  const row = asRecord(raw);
  const changes = Array.isArray(row.changes)
    ? row.changes.map(pickPortalChange).filter((change): change is PortalOfferChange => Boolean(change))
    : [];
  const round = Number(row.round);
  return {
    id: asString(row.id),
    round: Number.isFinite(round) && round > 0 ? round : 0,
    status: asString(row.status),
    expiresAt: asString(row.expires_at) || null,
    changes,
    premiums: Array.isArray(row.premiums)
      ? row.premiums.map(pickOfferPremium).filter((row): row is PortalOfferPremium => Boolean(row))
      : [],
    premiumsCurrency: asString(row.premiums_currency).trim(),
    documentReady: Boolean(row.document_ready),
    // Ausente (api-client viejo) = con firma, el comportamiento previo de MX.
    signatureRequired: row.signature_required !== false,
  };
}

function itemsFromPayload(payload: unknown): unknown[] {
  const root = asRecord(payload);
  const data = asRecord(root.data);
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(root.items)) return root.items;
  if (Array.isArray(payload)) return payload;
  return [];
}

function offerFromPayload(payload: unknown): unknown {
  const root = asRecord(payload);
  const data = asRecord(root.data);
  return data.offer ?? null;
}

/**
 * GET {REACT_APP_BACKEND_API}intake/sessions/{quoteId}/offer. Open offer or null.
 *
 * Canal del cliente: api-client ya responde solo la oferta ABIERTA y con la
 * lista cerrada de campos del portal. NO usar `…/intake/applications/{id}/offers`
 * (lectura del workbench): trae justification, pricing y la nota interna de
 * cancelación, y esta llamada sale del navegador — todo lo que responde queda
 * a la vista. `pickPortalOffer` sigue filtrando como segunda red.
 */
export async function getPortalOffer(quoteId: string): Promise<PortalOffer | null> {
  const id = quoteId.trim();
  if (!id) return null;

  try {
    const response = await fetch(`${BASE_URL}intake/sessions/${encodeURIComponent(id)}/offer`, {
      method: 'GET',
      headers: getHeadersApplication(),
    });

    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      const body = await parseJsonSafe(response);
      throw new PortalHttpError(
        messageFromBody(body, `Failed to load portal offer: HTTP ${response.status}`),
        response.status,
      );
    }

    const offer = offerFromPayload(await response.json());
    if (!offer || asString(asRecord(offer).status).toLowerCase() !== 'open') return null;
    return pickPortalOffer(offer);
  } catch (error) {
    handleServiceError(error);
    throw error instanceof Error ? error : new Error('Failed to load portal offer');
  }
}

/**
 * Catálogos que el portal lee de api-client (`GET /intake/catalogs/{name}`):
 * exclusiones (publicado por el Studio) y coberturas adicionales (estático,
 * claves del tarifador — ETAPA-1). Caché por sesión y por nombre. Fallo → {}:
 * el portal muestra el código crudo antes que romperse.
 */
export type PortalCatalogName = 'uwOfferExclusions' | 'uwOfferCoverages';

const catalogLabelsCache: Partial<Record<PortalCatalogName, Record<string, string>>> = {};
const catalogLabelsInFlight: Partial<Record<PortalCatalogName, Promise<Record<string, string>>>> = {};

const PORTAL_CATALOG_NAMES: PortalCatalogName[] = ['uwOfferExclusions', 'uwOfferCoverages'];

export function resetOfferLabelsCache(): void {
  // Hallazgo de review (#523): iterar solo `Object.keys(catalogLabelsCache)` deja afuera
  // un catálogo todavía in-flight (pedido pero sin resolver) que nunca llegó a escribirse
  // en catalogLabelsCache — ese fetch viejo sobrevive al reset y su promesa cacheada se le
  // sigue devolviendo a quien pida el catálogo después.
  PORTAL_CATALOG_NAMES.forEach((name) => {
    delete catalogLabelsCache[name];
    delete catalogLabelsInFlight[name];
  });
}

export async function getCatalogLabels(name: PortalCatalogName): Promise<Record<string, string>> {
  const cached = catalogLabelsCache[name];
  if (cached) return cached;
  const inFlight = catalogLabelsInFlight[name];
  if (inFlight) return inFlight;

  const request = (async () => {
    try {
      const response = await fetch(`${BASE_URL}intake/catalogs/${name}`, {
        method: 'GET',
        headers: getHeadersApplication(),
      });
      if (!response.ok) return {};
      const payload = await response.json();
      const labels: Record<string, string> = {};
      itemsFromPayload(payload).forEach((item) => {
        const row = asRecord(item);
        const code = asString(row.code).trim();
        const label = asString(row.label).trim();
        if (code && label) labels[code] = label;
      });
      catalogLabelsCache[name] = labels;
      return labels;
    } catch {
      return {};
    } finally {
      delete catalogLabelsInFlight[name];
    }
  })();
  catalogLabelsInFlight[name] = request;
  return request;
}

/** Nombres de las coberturas adicionales (catálogo `uwOfferCoverages`). */
export function getCoverageLabels(): Promise<Record<string, string>> {
  return getCatalogLabels('uwOfferCoverages');
}

/**
 * Etiquetas para los ítems de una oferta: exclusiones + coberturas, en un solo
 * mapa. Una oferta puede quitar coberturas Y agregar exclusiones, y la fila que
 * las dibuja no distingue de qué catálogo sale cada código.
 */
export async function getOfferLabels(): Promise<Record<string, string>> {
  const [exclusions, coverages] = await Promise.all([
    getCatalogLabels('uwOfferExclusions'),
    getCatalogLabels('uwOfferCoverages'),
  ]);
  return { ...coverages, ...exclusions };
}

export function offerItemLabel(item: PortalOfferItem, labels: Record<string, string> = {}): string {
  const name = item.text && item.text.trim() ? item.text.trim() : labels[item.code] || item.code;
  if (item.amount && item.amount > 0) {
    return `${name} · ${formatOfferMoney(item.amount)}`;
  }
  return name;
}

/**
 * POST compartido de los dos escritos del portal (aceptar/rechazar oferta,
 * confirmar 03E). Antes cada uno repetía el mismo esqueleto fetch/parse-error
 * (hallazgo del review de OUW-70): un cambio al contrato de error tenía que
 * aplicarse dos veces. `fallbackMessage` es lo único que distingue un POST
 * del otro; el body de éxito no se lee — ninguno de los dos casos lo usa.
 */
async function postPortal(path: string, body: string, fallbackMessage: string): Promise<void> {
  try {
    const response = await fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      headers: getHeadersApplication(),
      body,
    });

    if (!response.ok) {
      const parsedBody = await parseJsonSafe(response);
      throw new PortalHttpError(messageFromBody(parsedBody, `${fallbackMessage}: HTTP ${response.status}`), response.status);
    }
  } catch (error) {
    handleServiceError(error);
    throw error instanceof Error ? error : new Error(fallbackMessage);
  }
}

/** POST /intake/sessions/{quoteId}/offer-outcome. Throws PortalHttpError with status. */
export async function postOfferOutcome(quoteId: string, outcome: OfferOutcome): Promise<void> {
  const id = quoteId.trim();
  if (!id) throw new PortalHttpError('Missing quote id', 400);
  await postPortal(
    `intake/sessions/${encodeURIComponent(id)}/offer-outcome`,
    JSON.stringify({ outcome }),
    'Failed to submit offer outcome',
  );
}

/**
 * POST /intake/sessions/{quoteId}/confirm (OUW-70).
 * MX aprobación tal cual: awaiting_confirmation → confirmed. Empty body.
 * Throws PortalHttpError with status (409 when not awaiting confirmation).
 *
 * Devuelve void: el único caller (`BoardPage.handleConfirmSession`) refetchea
 * el estado por su cuenta después de esto y nunca leyó el `{quoteId, state}`
 * que esta función devolvía antes — un contrato sin consumidor (hallazgo del
 * review de OUW-70).
 */
export async function postConfirm(quoteId: string): Promise<void> {
  const id = quoteId.trim();
  if (!id) throw new PortalHttpError('Missing quote id', 400);
  await postPortal(`intake/sessions/${encodeURIComponent(id)}/confirm`, '{}', 'Failed to confirm session');
}

/**
 * OUW-181: abre el PDF de la propuesta en una pestaña nueva.
 *
 * Pestaña y no descarga forzada: en escritorio el navegador lo muestra con su
 * visor, y en el teléfono —donde se usa mucho este portal— deja elegir entre
 * verlo o guardarlo. Un visor embebido en móvil es peor que las dos.
 *
 * La pestaña se abre ANTES del fetch, no después. Los navegadores solo
 * permiten `window.open` dentro del gesto del click; si se espera la respuesta
 * primero, el gesto ya se perdió y el bloqueador de pop-ups la cancela — en
 * celular, casi siempre. Se abre vacía, se le carga la URL cuando el PDF llega,
 * y si el usuario cancela se cierra.
 *
 * api-client lo genera al vuelo si todavía no está, así que este pedido puede
 * tardar unos segundos la primera vez.
 *
 * SIN `noopener` (OUW-200, hallazgo H1): con esa feature `window.open` devuelve
 * `null` por especificación, en todos los navegadores, y la guarda de abajo lo
 * leía como pop-up bloqueado — o sea, siempre: pestaña en blanco y el aviso de
 * error, sin llegar nunca a pedir el PDF. El handle hace falta para cargarle el
 * blob, y es una pestaña nuestra del mismo origen: no hay nada que aislar.
 */
export async function openOfferDocument(quoteId: string): Promise<void> {
  const id = quoteId.trim();
  if (!id) throw new PortalHttpError('Missing quote id', 400);

  const tab = window.open('', '_blank');
  if (!tab) {
    // Bloqueada por el navegador. Decirlo es mejor que no hacer nada: quien
    // llama muestra el aviso y el cliente puede habilitar los pop-ups.
    throw new PortalHttpError('Popup blocked', 0);
  }

  try {
    const response = await fetch(
      `${BASE_URL}intake/sessions/${encodeURIComponent(id)}/offer/document.pdf`,
      { method: 'GET', headers: getHeadersApplication() },
    );
    if (!response.ok) {
      const body = await parseJsonSafe(response);
      throw new PortalHttpError(
        messageFromBody(body, `Failed to load offer document: HTTP ${response.status}`),
        response.status,
      );
    }

    const url = URL.createObjectURL(await response.blob());
    tab.location.href = url;
    // El objeto vive hasta que la pestaña lo terminó de leer. Revocarlo en el
    // acto la deja en blanco en algunos navegadores.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (error) {
    // Sin esto queda una pestaña en blanco abierta cada vez que falla.
    tab.close();
    throw error;
  }
}

/**
 * Mismo locale que `formatPortalMoney` a propósito: con dos locales distintos,
 * el MISMO monto se veía `$500,000` en la tarjeta lateral y `MX$500,000` en el
 * panel de la oferta, en la misma pantalla. `en-US` es el que rinde USD como
 * `$…`, que es lo que pide el diseño.
 */
export function formatOfferMoney(amount: number, currency = 'USD'): string {
  const fractionDigits = Number.isInteger(amount) ? 0 : 2;
  const code = currency.trim() || 'USD';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount} ${code}`;
  }
}

export function formatOfferPremium(money: PortalOfferMoney | null | undefined): string {
  if (!money) return '';
  return `${formatOfferMoney(money.amount, money.currency)}${frequencySuffix(money.frequency)}`;
}

export function formatOfferAmount(money: PortalOfferMoney | null | undefined): string {
  if (!money) return '';
  return formatOfferMoney(money.amount, money.currency);
}

export const PORTAL_REQUIREMENT_ANALYST_FIELDS = [
  'analyst_label',
  'review_status',
  'review_comment',
  'reviewed_by',
  'reviewed_at',
  'origin',
  'decision_id',
  'last_actor_type',
  'replaces_requirement_id',
  'closed_reason',
] as const;

/** Closed list from api-client OUW-92 (`schedule` | `upload` | `respond`). */
export const PORTAL_CLIENT_ACTIONS = ['schedule', 'upload', 'respond'] as const;
export type PortalClientAction = (typeof PORTAL_CLIENT_ACTIONS)[number];

/** Terminal statuses the client must not see. `completed` stays (row → «En evaluación»). */
const PORTAL_HIDDEN_REQUIREMENT_STATUSES = new Set(['cancelled', 'waived']);

export function isHiddenPortalRequirementStatus(status: string): boolean {
  return PORTAL_HIDDEN_REQUIREMENT_STATUSES.has((status || '').toLowerCase());
}

export const REQ_FILE_MAX_BYTES = 50 * 1024 * 1024;
const REQ_FILE_TYPES = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
]);
const REQ_FILE_TYPE_BY_EXT: Record<string, string> = {
  '.pdf': 'application/pdf',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.webp': 'image/webp',
  '.heic': 'image/heic',
};
export const REQ_FILE_ACCEPT =
  'application/pdf,image/jpeg,image/png,image/webp,image/heic,.pdf,.jpg,.jpeg,.png,.webp,.heic';
export const REQ_FILE_CAMERA_ACCEPT =
  'image/jpeg,image/png,image/webp,image/heic,.jpg,.jpeg,.png,.webp,.heic';

export type PortalRequirementPart = {
  code: string;
  name: string;
  required: boolean;
  covered: boolean;
};

export type PortalRequirementFile = {
  id: string;
  originalName: string;
  contentType: string;
  sizeBytes: number;
  partCode: string;
  url: string;
};

export type PortalRequirementResource = {
  kind: string;
  title: string;
  url: string;
};

export type PortalRequirement = {
  id: string;
  /**
   * Código del catálogo de api-client (`interview`, `ole_medical_exam`, …).
   * OUW-336: el modal de horarios lo usa para saber si es la videollamada o
   * el examen médico.
   */
  code: string;
  kind: string;
  status: string;
  /** Resolved client title (API `client_label`; legacy `canonical_text`). */
  clientLabel: string;
  /** How-to copy (API `client_detail`). */
  clientDetail: string;
  noteToClient: string;
  clientResponse: string;
  /** Evidence flags — still on the wire; close the deliver POST. */
  requiresFile: boolean;
  requiresResponse: boolean;
  /** Server-derived: open AND has upload/respond (OUW-92 Q7/Q15). */
  canDeliver: boolean;
  clientActions: PortalClientAction[];
  parts: PortalRequirementPart[];
  files: PortalRequirementFile[];
  lastTransitionAt: string | null;
  requestedAt: string;
  resources: PortalRequirementResource[];
  /**
   * Antigüedad máxima del documento, en días (api-client `max_age_days`).
   * null = sin límite. El portal la muestra en meses o años cuando el número
   * cierra exacto.
   */
  maxAgeDays: number | null;
  /**
   * OUW-260: horarios que ESTE cliente ya propuso. null = todavía no propuso.
   * Vive en `details.availability` del requisito (api-client).
   */
  availability: PortalRequirementAvailability | null;
  /** La cita que el analista agendó, si ya eligió. */
  scheduledAt: string | null;
  /** Link de la videollamada, solo cuando hay cita. */
  meetingUrl: string;
};

/** OUW-260: lo que el cliente propuso. La reunión dura `durationMinutes`. */
export type PortalRequirementAvailability = {
  durationMinutes: number;
  /** Los COMIENZOS propuestos, ISO en UTC y ordenados. */
  options: string[];
  note: string;
  proposedAt: string;
};

function firstNonEmpty(...values: unknown[]): string {
  for (const value of values) {
    const text = asString(value).trim();
    if (text) return text;
  }
  return '';
}

export function pickClientLabel(row: Record<string, unknown>): string {
  return firstNonEmpty(
    row.client_label,
    row.clientLabel,
    row.canonical_text,
    row.canonicalText,
  );
}

export function hasPortalClientAction(
  actions: readonly string[],
  action: PortalClientAction,
): boolean {
  return actions.includes(action);
}

function getHeadersMultipart(): Headers {
  const headers = getHeadersApplication();
  headers.delete('Content-Type');
  return headers;
}

function pickRequirementPart(raw: unknown): PortalRequirementPart | null {
  const row = asRecord(raw);
  const code = asString(row.code).trim();
  if (!code) return null;
  return {
    code,
    name: asString(row.name),
    required: Boolean(row.required),
    covered: Boolean(row.covered),
  };
}

export function pickPortalRequirementFile(raw: unknown): PortalRequirementFile | null {
  const row = asRecord(raw);
  const status = asString(row.status).toLowerCase();
  if (status && status !== 'active') return null;
  const id = asString(row.id).trim();
  if (!id) return null;
  const sizeBytes = Number(row.size_bytes);
  return {
    id,
    originalName: asString(row.original_name),
    contentType: asString(row.content_type),
    sizeBytes: Number.isFinite(sizeBytes) ? sizeBytes : 0,
    partCode: asString(row.part_code),
    url: asString(row.url),
  };
}

function pickResourceItem(raw: unknown): PortalRequirementResource | null {
  const row = asRecord(raw);
  const url = asString(row.url).trim();
  if (!url) return null;
  return {
    kind: asString(row.kind).trim() || 'link',
    title: firstNonEmpty(row.title, row.message),
    url,
  };
}

function pickLegacyDetailResource(raw: unknown): PortalRequirementResource | null {
  let details: Record<string, unknown> = {};
  if (typeof raw === 'string') {
    try {
      details = asRecord(JSON.parse(raw) as unknown);
    } catch {
      return null;
    }
  } else {
    details = asRecord(raw);
  }
  return pickResourceItem(details.resource);
}

function pickRequirementResources(row: Record<string, unknown>): PortalRequirementResource[] {
  if (Array.isArray(row.resources)) {
    const items = row.resources
      .map(pickResourceItem)
      .filter((item): item is PortalRequirementResource => Boolean(item));
    if (items.length) return items;
  }
  const legacy = pickLegacyDetailResource(row.details);
  return legacy ? [legacy] : [];
}

function pickClientActions(row: Record<string, unknown>): PortalClientAction[] {
  const raw = row.client_actions ?? row.clientActions;
  if (Array.isArray(raw)) {
    const seen = new Set<PortalClientAction>();
    const out: PortalClientAction[] = [];
    for (const item of raw) {
      const action = asString(item).trim().toLowerCase();
      if (!PORTAL_CLIENT_ACTIONS.includes(action as PortalClientAction) || seen.has(action as PortalClientAction)) {
        continue;
      }
      seen.add(action as PortalClientAction);
      out.push(action as PortalClientAction);
    }
    return out;
  }
  const out: PortalClientAction[] = [];
  if (row.requires_file) out.push('upload');
  if (row.requires_response) out.push('respond');
  return out;
}

function pickCanDeliver(
  row: Record<string, unknown>,
  status: string,
  actions: readonly PortalClientAction[],
): boolean {
  if (typeof row.can_deliver === 'boolean') return row.can_deliver;
  if (typeof row.canDeliver === 'boolean') return row.canDeliver;
  return status === 'requested' && (
    hasPortalClientAction(actions, 'upload') || hasPortalClientAction(actions, 'respond')
  );
}

export function pickPortalRequirement(raw: unknown): PortalRequirement {
  const row = asRecord(raw);
  const files = Array.isArray(row.files)
    ? row.files.map(pickPortalRequirementFile).filter((file): file is PortalRequirementFile => Boolean(file))
    : [];
  const parts = Array.isArray(row.parts)
    ? row.parts.map(pickRequirementPart).filter((part): part is PortalRequirementPart => Boolean(part))
    : [];
  const status = asString(row.status).toLowerCase();
  const clientActions = pickClientActions(row);
  return {
    id: asString(row.id),
    code: asString(row.code).trim(),
    kind: asString(row.kind),
    status,
    clientLabel: pickClientLabel(row),
    clientDetail: firstNonEmpty(row.client_detail, row.clientDetail),
    noteToClient: asString(row.note_to_client),
    clientResponse: asString(row.client_response),
    requiresFile: Boolean(row.requires_file),
    requiresResponse: Boolean(row.requires_response),
    canDeliver: pickCanDeliver(row, status, clientActions),
    clientActions,
    parts,
    files,
    lastTransitionAt: asString(row.last_transition_at) || null,
    requestedAt: asString(row.requested_at),
    resources: pickRequirementResources(row),
    maxAgeDays: pickMaxAgeDays(row),
    availability: pickRequirementAvailability(row),
    scheduledAt: firstNonEmpty(row.scheduled_at, row.scheduledAt) || null,
    meetingUrl: firstNonEmpty(row.meeting_url, row.meetingUrl),
  };
}

/**
 * Lee `details.availability`. Tolerante a propósito: una fila vieja, un
 * `details` vacío o un documento con otra forma devuelven null, que el portal
 * lee como «todavía no propuso» — nunca como un error de pantalla.
 */
function pickMaxAgeDays(row: Record<string, unknown>): number | null {
  const raw = row.max_age_days ?? row.maxAgeDays;
  if (raw == null || raw === '') return null;
  const days = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(days) || days <= 0) return null;
  return Math.trunc(days);
}

function pickRequirementAvailability(
  row: Record<string, unknown>,
): PortalRequirementAvailability | null {
  const details = asRecord(row.details);
  const raw = asRecord(details.availability);
  const options = Array.isArray(raw.options)
    ? raw.options.map((option) => asString(option)).filter(Boolean)
    : [];
  if (options.length === 0) return null;
  const duration = Number(raw.duration_minutes ?? raw.durationMinutes);
  return {
    durationMinutes: Number.isFinite(duration) && duration > 0 ? duration : 60,
    options,
    note: asString(raw.note),
    proposedAt: asString(raw.proposed_at ?? raw.proposedAt),
  };
}

function requirementRank(status: string): number {
  return status === 'requested' ? 0 : 1;
}

function requirementMediaType(type: string): string {
  return (type || '').split(';')[0].trim().toLowerCase();
}

function requirementTypeFromName(name: string): string | undefined {
  const lower = (name || '').toLowerCase();
  const dot = lower.lastIndexOf('.');
  if (dot < 0) return undefined;
  return REQ_FILE_TYPE_BY_EXT[lower.slice(dot)];
}

export function isAllowedRequirementFile(
  file: { name: string; type: string; size: number },
): { ok: true; contentType: string } | { ok: false; message: string } {
  if (file.size <= 0) {
    return { ok: false, message: 'El archivo está vacío.' };
  }
  if (file.size > REQ_FILE_MAX_BYTES) {
    return { ok: false, message: 'El archivo supera los 50 MB.' };
  }
  const type = requirementMediaType(file.type);
  if (REQ_FILE_TYPES.has(type)) return { ok: true, contentType: type };
  const mapped = requirementTypeFromName(file.name);
  if ((type === '' || type === 'application/octet-stream') && mapped) {
    return { ok: true, contentType: mapped };
  }
  return { ok: false, message: 'Use PDF, JPG, PNG, WEBP o HEIC.' };
}

function fileForRequirementUpload(file: File): File {
  const allowed = isAllowedRequirementFile(file);
  if (!allowed.ok) throw new PortalHttpError(allowed.message, 400);
  if (requirementMediaType(file.type) === allowed.contentType) return file;
  return new File([file], file.name, { type: allowed.contentType, lastModified: file.lastModified });
}

function requirementsUrl(quoteId: string, suffix = ''): string {
  return `${BASE_URL}intake/applications/${encodeURIComponent(quoteId)}/requirements${suffix}`;
}

function throwPortalHttp(body: unknown, status: number, fallback: string): never {
  throw new PortalHttpError(messageFromBody(body, fallback), status);
}

/** GET …/intake/applications/{quoteId}/requirements. 404 → []. Hides cancelled and waived. */
export async function getPortalRequirements(quoteId: string): Promise<PortalRequirement[]> {
  const id = quoteId.trim();
  if (!id) return [];

  try {
    const response = await fetch(requirementsUrl(id), {
      method: 'GET',
      headers: getHeadersApplication(),
    });

    if (response.status === 404) return [];
    if (!response.ok) {
      const body = await parseJsonSafe(response);
      throwPortalHttp(body, response.status, `Failed to load portal requirements: HTTP ${response.status}`);
    }

    const payload = await response.json();
    return itemsFromPayload(payload)
      .map(pickPortalRequirement)
      .filter((item) => item.id && !isHiddenPortalRequirementStatus(item.status))
      .sort((a, b) => {
        const rank = requirementRank(a.status) - requirementRank(b.status);
        if (rank !== 0) return rank;
        return a.requestedAt.localeCompare(b.requestedAt);
      });
  } catch (error) {
    handleServiceError(error);
    throw error instanceof Error ? error : new Error('Failed to load portal requirements');
  }
}

export async function uploadRequirementFile(
  quoteId: string,
  requirementId: string,
  file: File,
  partCode?: string,
): Promise<PortalRequirementFile> {
  const id = quoteId.trim();
  const reqId = requirementId.trim();
  if (!id || !reqId) throw new PortalHttpError('Missing quote or requirement id', 400);

  const form = new FormData();
  form.append('file', fileForRequirementUpload(file));
  if (partCode) form.append('part_code', partCode);

  try {
    const response = await fetch(requirementsUrl(id, `/${encodeURIComponent(reqId)}/files`), {
      method: 'POST',
      headers: getHeadersMultipart(),
      body: form,
    });
    const body = await parseJsonSafe(response);
    if (!response.ok) {
      throwPortalHttp(body, response.status, `Failed to upload requirement file: HTTP ${response.status}`);
    }
    // El backend envuelve el archivo en `data.file`; toleramos también `data`
    // pelado y la raíz por si el contrato se aplana.
    const data = asRecord(asRecord(body).data);
    const nested = asRecord(data.file);
    const source = Object.keys(nested).length ? nested : data;
    const picked = pickPortalRequirementFile(Object.keys(source).length ? source : body);
    if (!picked) throw new PortalHttpError('Upload did not return a file', 500);
    return picked;
  } catch (error) {
    handleServiceError(error);
    throw error instanceof Error ? error : new Error('Failed to upload requirement file');
  }
}

export async function deleteRequirementFile(
  quoteId: string,
  requirementId: string,
  fileId: string,
): Promise<void> {
  const id = quoteId.trim();
  const reqId = requirementId.trim();
  const fid = fileId.trim();
  if (!id || !reqId || !fid) throw new PortalHttpError('Missing quote, requirement or file id', 400);

  try {
    const response = await fetch(
      requirementsUrl(id, `/${encodeURIComponent(reqId)}/files/${encodeURIComponent(fid)}`),
      { method: 'DELETE', headers: getHeadersApplication() },
    );
    if (response.status === 404) return;
    if (!response.ok) {
      const body = await parseJsonSafe(response);
      throwPortalHttp(body, response.status, `Failed to delete requirement file: HTTP ${response.status}`);
    }
  } catch (error) {
    handleServiceError(error);
    throw error instanceof Error ? error : new Error('Failed to delete requirement file');
  }
}

/**
 * OUW-260: el cliente propone horarios para la videollamada.
 *
 * Reemplaza lo propuesto antes: se manda la lista entera, no un parche. El
 * backend valida la grilla (hora en punto o y media, margen mínimo, hasta
 * tres) y responde 400 con el motivo.
 */
export async function proposeRequirementAvailability(
  quoteId: string,
  requirementId: string,
  options: string[],
  note?: string,
): Promise<void> {
  const id = quoteId.trim();
  const reqId = requirementId.trim();
  if (!id || !reqId) throw new PortalHttpError('Missing quote or requirement id', 400);

  try {
    const response = await fetch(
      requirementsUrl(id, `/${encodeURIComponent(reqId)}/availability`),
      {
        method: 'PUT',
        headers: getHeadersApplication(),
        body: JSON.stringify({ options, note: note || '' }),
      },
    );
    if (!response.ok) {
      const body = await parseJsonSafe(response);
      throwPortalHttp(body, response.status, `Failed to propose availability: HTTP ${response.status}`);
    }
  } catch (error) {
    handleServiceError(error);
    throw error instanceof Error ? error : new Error('Failed to propose availability');
  }
}

export async function deliverRequirement(
  quoteId: string,
  requirementId: string,
  clientResponse?: string,
): Promise<void> {
  const id = quoteId.trim();
  const reqId = requirementId.trim();
  if (!id || !reqId) throw new PortalHttpError('Missing quote or requirement id', 400);

  try {
    const response = await fetch(requirementsUrl(id, `/${encodeURIComponent(reqId)}/deliver`), {
      method: 'POST',
      headers: getHeadersApplication(),
      body: JSON.stringify({ client_response: clientResponse || '' }),
    });
    if (!response.ok) {
      const body = await parseJsonSafe(response);
      throwPortalHttp(body, response.status, `Failed to deliver requirement: HTTP ${response.status}`);
    }
  } catch (error) {
    handleServiceError(error);
    throw error instanceof Error ? error : new Error('Failed to deliver requirement');
  }
}

/**
 * UWT-79: si aceptar esta oferta pasa por la firma. Lo decide api-client
 * (`signature_required`); sin oferta o sin el campo, se asume firma (MX).
 */
export function offerRequiresSignature(offer: PortalOffer | null | undefined): boolean {
  return offer?.signatureRequired !== false;
}

/** UWT-79: si la solicitud es de un mercado con firma. Sin dato = firma (MX). */
export function applicationRequiresSignature(application: PortalStatus | null | undefined): boolean {
  return application?.signatureRequired !== false;
}
